import { useSelector } from 'react-redux'
import { Customer } from '@typesApp/Session'
import { ActiveClaimCheckStatus } from '@typesApp/ClaimRecord'
import {
  authenticatedSelector,
  authTokenErrorSelector,
  authTokenReadySelector,
  authTokenSessionIdSelector,
  connectorOrderCompleteSelector,
  connectorSessionCustomerSelector,
  connectorSessionIdSelector,
  isAnonymousUserSelector,
  isGuestUserSelector,
  isSessionInitializingSelector,
  loggingOutSelector,
  sessionExpiredSelector,
  sessionReadySelector,
} from '@redux/slices/connectorSlice'
import {
  activeClaimCheckStatusSelector,
  restrictedCheckPerformedSelector,
  restrictedStatusSelector,
} from '@redux/slices/claimStatusSlice'
import { memberEligibilityCheckInProgressSelector } from '@redux/slices/insuranceMemberSlice'

type UseSessionInfoReturn = {
  isAnonymousUser?: boolean
  isGuestUser?: boolean
  customer?: Customer
  sessionId?: string
  authTokenSessionId?: string
  isTokenError?: boolean
  isTokenReady?: boolean
  isLoggingOut?: boolean
  isAuthenticated?: boolean
  isSessionReady?: boolean
  isSessionExpired?: boolean
  isOrderComplete?: boolean
  isActiveDataChecked?: boolean
  isRestricted?: boolean
  isRestrictedCheckPerformed?: boolean
  isSessionInitializing?: boolean
  isMemberEligibilityCheckInProgress?: boolean
  isAwaitingLogin?: boolean
}

export const useSessionInfo = (): UseSessionInfoReturn => {
  const isAnonymousUser = useSelector(isAnonymousUserSelector)
  const isGuestUser = useSelector(isGuestUserSelector)
  const sessionId = useSelector(connectorSessionIdSelector)
  const authTokenSessionId = useSelector(authTokenSessionIdSelector)
  const customer = useSelector(connectorSessionCustomerSelector)
  const isTokenError = useSelector(authTokenErrorSelector)
  const isTokenReady = useSelector(authTokenReadySelector)
  const isLoggingOut = useSelector(loggingOutSelector)
  const isSessionReady = useSelector(sessionReadySelector)
  const isSessionExpired = useSelector(sessionExpiredSelector)
  const isOrderComplete = useSelector(connectorOrderCompleteSelector)
  const isAuthenticated = useSelector(authenticatedSelector)
  const activeDataStatus = useSelector(activeClaimCheckStatusSelector)
  const isRestricted = useSelector(restrictedStatusSelector)
  const isRestrictedCheckPerformed = useSelector(restrictedCheckPerformedSelector)
  const isSessionInitializing = useSelector(isSessionInitializingSelector)
  const isMemberEligibilityCheckInProgress = useSelector(memberEligibilityCheckInProgressSelector)

  return {
    isAnonymousUser,
    isGuestUser,
    customer,
    sessionId,
    authTokenSessionId,
    isTokenError,
    isTokenReady,
    isLoggingOut,
    isSessionReady,
    isSessionExpired,
    isOrderComplete,
    isAuthenticated,
    isActiveDataChecked: activeDataStatus === ActiveClaimCheckStatus.COMPLETE,
    isRestricted,
    isRestrictedCheckPerformed,
    isSessionInitializing,
    isMemberEligibilityCheckInProgress,
    isAwaitingLogin: !isAuthenticated && !isGuestUser && Boolean(sessionId),
  }
}
